import StatWrapper from "../shared/StatWrapper";
import { useState } from "react";
import CoverLink from "components/Cover/CoverLink";
import Heading from "hardcover-ui/components/Heading";
import { motion, AnimatePresence } from "framer-motion";
import Button from "hardcover-ui/components/Button";
import Badge from "hardcover-ui/components/Badge";
import Text from "hardcover-ui/components/Text";
import Icon from "hardcover-ui/components/Icon";
import UpIcon from "icons/solid/circle-chevron-up.svg";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import LocaleNumber from "components/ui/LocaleNumber";
import { BookSerializersBookByline } from "types/serializers";

interface BooksReadStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

function getCoverSize(count: number) {
  if (count <= 5) {
    return "lg";
  } else if (count <= 10) {
    return "md";
  } else if (count <= 20) {
    return "sm";
  } else {
    return "xs";
  }
}

export default function BooksReadStat({ size = "lg" }: BooksReadStatProps) {
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { booksRead: data } = stats;
  const { theme } = useChartTheme();

  if (!data || !data.books || data.books.length === 0) {
    return (
      <StatWrapper title="Books Read" size={size}>
        <div className="flex flex-col w-full gap-4">
          <Heading>
            {isOwner ? "You" : user.name} haven't read any books yet
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const [visibleBooks, setVisibleBooks] = useState<BookSerializersBookByline[]>(
    data.books.slice(0, 22)
  );
  const [coverSize, setCoverSize] = useState<"xs" | "sm" | "md" | "lg">(
    getCoverSize(data.books.length > 22 ? 22 : data.books.length)
  );

  const loadMore = () => {
    const books = data.books.slice(0, visibleBooks.length + 22);
    setVisibleBooks(books);
    setCoverSize(getCoverSize(books.length));
  };

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        type: "spring",
      },
    },
  };

  const item = {
    hidden: { x: 20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
    },
  };

  return (
    <StatWrapper title="Books Read" size={size}>
      <div className="flex flex-col w-full gap-4">
        <div className="flex justify-between items-end">
          <Heading>
            {isOwner ? "You" : user.name} read{" "}
            <LocaleNumber number={data.booksRead || 0} /> books
          </Heading>
          {(data.percentile || 0) > 0 ? (
            <Text>
              Top{" "}
              <Badge variant={theme === "colorful" ? "success" : "secondary"}>
                <Icon icon={UpIcon} size="sm" className="mb-px" />{" "}
                {data.percentile || 0}%
              </Badge>{" "}
              of readers
            </Text>
          ) : null}
        </div>
        <AnimatePresence>
          <motion.div
            className="flex flex-wrap gap-1 items-center w-full"
            variants={container}
            initial="hidden"
            animate="visible"
          >
            {visibleBooks.map((book, index) => (
              <motion.div
                key={`book-${book.slug}`}
                variants={item}
                custom={index}
              >
                <CoverLink
                  book={book}
                  size={coverSize}
                  variant="none"
                  settings={{ tooltip: true, details: false }}
                  className={theme === "monochrome" ? "grayscale" : ""}
                />
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
        {visibleBooks.length < (data.books?.length || 0) && (
          <Button variant="tertiary" size="sm" onClick={loadMore}>
            Load More
          </Button>
        )}
      </div>
    </StatWrapper>
  );
}
